import { motion } from "framer-motion";
import { X, Check, TrendingDown, TrendingUp } from "lucide-react";

const BEFORE = [
  "Slow, outdated website",
  "Poor Google rankings",
  "Weak, inconsistent branding",
  "Few qualified leads",
  "Invisible to AI search",
];

const AFTER = [
  "Lightning-fast 99/100 site",
  "Top of Google + Maps",
  "AI Visibility across ChatGPT, Gemini, Perplexity",
  "Premium, cohesive branding",
  "Consistent inbound pipeline",
];

function MiniChart({ down }) {
  const points = down
    ? "0,40 20,42 40,38 60,46 80,50 100,55"
    : "0,55 20,45 40,40 60,28 80,18 100,8";
  return (
    <svg viewBox="0 0 100 60" className="w-full h-24">
      <defs>
        <linearGradient id={down ? "g-down" : "g-up"} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={down ? "#71717A" : "#00E5FF"} stopOpacity="0.4" />
          <stop offset="100%" stopColor={down ? "#71717A" : "#00E5FF"} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        fill="none"
        stroke={down ? "#71717A" : "#00E5FF"}
        strokeWidth="1.8"
        points={points}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        fill={`url(#${down ? "g-down" : "g-up"})`}
        stroke="none"
        points={`${points} 100,60 0,60`}
      />
    </svg>
  );
}

export default function BeforeAfter() {
  return (
    <section
      data-testid="before-after"
      className="section relative bg-gradient-to-b from-transparent via-white/[0.015] to-transparent"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
            Before / After
          </div>
          <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
            What your business looks like, before and after.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* BEFORE */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-8"
            data-testid="before-card"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs uppercase tracking-widest text-white/40 font-semibold">
                Before
              </div>
              <div className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/5 text-white/50 border border-white/10">
                <TrendingDown className="w-3 h-3" /> Flatlined
              </div>
            </div>
            <MiniChart down />
            <ul className="mt-6 space-y-3">
              {BEFORE.map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/60">
                  <span className="mt-1 w-5 h-5 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                    <X className="w-3 h-3 text-white/40" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* AFTER */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative rounded-3xl p-[1px] bg-gradient-to-br from-blue-600 to-cyan-400"
            data-testid="after-card"
          >
            <div className="rounded-3xl bg-[#0a0a0a] p-8 h-full">
              <div className="flex items-center justify-between mb-6">
                <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">
                  After EchoWings
                </div>
                <div className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                  <TrendingUp className="w-3 h-3" /> Compounding
                </div>
              </div>
              <MiniChart />
              <ul className="mt-6 space-y-3">
                {AFTER.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-white"
                  >
                    <span className="mt-1 w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-black" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
